import React, { useState, useEffect, useRef } from "react";

const C = {
  bg: "#1C3A28", forest: "#2D5A3D", dark: "#0a1a10",
  cream: "#F8F6F1", mint: "#9FE1CB", terra: "#C4622D",
  ok: "#2D9E4A", warn: "#D97706", danger: "#DC2626",
};

/* ── Outil 1 : Niveau à bulle ───────────────────────────────────── */
function NiveauBulle({ t = {} }) {
  const [active,   setActive]   = useState(false);
  const [gamma,    setGamma]    = useState(0);
  const [beta,     setBeta]     = useState(0);
  const [needPerm, setNeedPerm] = useState(false);

  useEffect(() => {
    if (typeof DeviceOrientationEvent !== "undefined" &&
        typeof DeviceOrientationEvent.requestPermission === "function") {
      setNeedPerm(true);
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    const h = (e) => {
      if (e.gamma !== null) setGamma(e.gamma);
      if (e.beta  !== null) setBeta(e.beta);
    };
    window.addEventListener("deviceorientation", h);
    return () => window.removeEventListener("deviceorientation", h);
  }, [active]);

  const requestPerm = async () => {
    try {
      const perm = await DeviceOrientationEvent.requestPermission();
      if (perm === "granted") { setActive(true); setNeedPerm(false); }
    } catch {}
  };

  const MAX = 80;
  const bx   = Math.max(-MAX, Math.min(MAX, gamma * 2.5));
  const by   = Math.max(-MAX, Math.min(MAX, beta  * 2.5));
  const dist = Math.sqrt(bx * bx + by * by);
  const bubbleColor = dist < 15 ? C.ok : dist < 30 ? C.warn : C.danger;

  const ag = Math.abs(gamma), ab = Math.abs(beta);
  let advice = { text: t.niveau_ok || "✅ Véhicule à niveau", color: C.ok };
  if (ag > 2 || ab > 2) {
    if (ag >= ab) {
      const h = (Math.round(ag * 0.8 / 0.5) * 0.5).toFixed(1);
      advice = gamma > 0
        ? { text: `${t.cale_gauche  || "Calez roue GAUCHE"} ≈ ${h} cm`, color: C.warn }
        : { text: `${t.cale_droite  || "Calez roue DROITE"} ≈ ${h} cm`, color: C.warn };
    } else {
      advice = beta > 0
        ? { text: t.cale_avant   || "Calez les roues AVANT",   color: C.warn }
        : { text: t.cale_arriere || "Calez les roues ARRIÈRE", color: C.warn };
    }
  }

  return (
    <div style={{ background: C.dark, borderRadius: 16, padding: 20 }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.mint, marginBottom: 14, textAlign: "center" }}>
        🔵 {t.outil_niveau || "Niveau à bulle"}
      </div>

      <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
        <div style={{
          width: 200, height: 200, borderRadius: "50%", position: "relative",
          background: "rgba(255,255,255,0.04)", border: "2px solid rgba(255,255,255,0.18)",
          overflow: "hidden",
        }}>
          <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "rgba(255,255,255,0.12)", transform: "translateY(-50%)" }} />
          <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 1, background: "rgba(255,255,255,0.12)", transform: "translateX(-50%)" }} />
          <div style={{
            position: "absolute", top: "50%", left: "50%",
            width: 56, height: 56, borderRadius: "50%",
            border: "1.5px solid rgba(255,255,255,0.22)",
            transform: "translate(-50%, -50%)",
          }} />
          {active ? (
            <div style={{
              position: "absolute", top: "50%", left: "50%",
              width: 26, height: 26, borderRadius: "50%",
              background: bubbleColor, boxShadow: `0 0 14px ${bubbleColor}88`,
              transform: `translate(calc(-50% + ${bx}px), calc(-50% + ${by}px))`,
              transition: "transform 0.08s linear, background 0.3s",
            }} />
          ) : (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.25)" }}>{t.inactif || "Inactif"}</span>
            </div>
          )}
        </div>
      </div>

      {active && (
        <>
          <div style={{
            background: `${advice.color}22`, border: `1px solid ${advice.color}44`,
            borderRadius: 10, padding: "9px 14px", marginBottom: 10, textAlign: "center",
            fontSize: 13, fontWeight: 700, color: advice.color,
          }}>
            {advice.text}
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 24, marginBottom: 12 }}>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>γ {gamma.toFixed(1)}°</span>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>β {beta.toFixed(1)}°</span>
          </div>
        </>
      )}

      {needPerm && !active ? (
        <button onClick={requestPerm} style={{
          width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
          background: C.mint, color: C.dark, fontSize: 13, fontWeight: 700,
          cursor: "pointer", fontFamily: "inherit",
        }}>
          🔓 {t.autoriser_capteur || "Autoriser le capteur (iOS)"}
        </button>
      ) : (
        <button onClick={() => setActive(a => !a)} style={{
          width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
          background: active ? `${C.danger}22` : C.mint,
          color: active ? C.danger : C.dark,
          fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
        }}>
          {active ? `⏹ ${t.desactiver || "Désactiver"}` : `▶ ${t.outil_niveau_btn || "Activer le niveau"}`}
        </button>
      )}
    </div>
  );
}

/* ── Outil 2 : Inclinomètre de sécurité ────────────────────────── */
function Inclinometre({ t = {} }) {
  const [active, setActive] = useState(false);
  const [gamma,  setGamma]  = useState(0);

  useEffect(() => {
    if (!active) return;
    const h = (e) => { if (e.gamma !== null) setGamma(e.gamma); };
    window.addEventListener("deviceorientation", h);
    return () => window.removeEventListener("deviceorientation", h);
  }, [active]);

  const ag     = Math.abs(gamma);
  const status = ag < 15 ? "ok" : ag < 25 ? "warn" : "danger";
  const col    = status === "ok" ? C.ok : status === "warn" ? C.warn : C.danger;

  // Semi-circle arc math (center bottom, arc from -85° to +85° going through top)
  const W = 220, H = 120, cx = 110, cy = H, R = 95;
  const pt = (deg) => {
    const rad = deg * Math.PI / 180;
    return { x: +(cx + R * Math.sin(rad)).toFixed(2), y: +(cy - R * Math.cos(rad)).toFixed(2) };
  };
  const arc = (a, b) => {
    const s = pt(a), e = pt(b);
    const large = Math.abs(b - a) > 180 ? 1 : 0;
    const sweep = b > a ? 1 : 0;
    return `M${s.x} ${s.y} A${R} ${R} 0 ${large} ${sweep} ${e.x} ${e.y}`;
  };
  const needleDeg = Math.max(-85, Math.min(85, gamma));
  const np = pt(needleDeg);

  return (
    <div style={{
      background: C.dark, borderRadius: 16, padding: 20,
      animation: status === "danger" && active ? "incl-pulse 0.5s infinite alternate" : "none",
    }}>
      <style>{`@keyframes incl-pulse{from{background:#0a1a10}to{background:rgba(220,38,38,.18)}}`}</style>

      <div style={{ fontSize: 15, fontWeight: 800, color: C.mint, marginBottom: 14, textAlign: "center" }}>
        📐 {t.outil_inclinometre || "Inclinomètre de sécurité"}
      </div>

      <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
        <svg width={W} height={H + 8} viewBox={`0 0 ${W} ${H + 8}`}>
          {/* Background track */}
          <path d={arc(-85, 85)} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="14" />
          {/* Green ±15° */}
          <path d={arc(-15, 15)} fill="none" stroke={C.ok}     strokeWidth="12" strokeOpacity=".85" />
          {/* Orange ±15-25° */}
          <path d={arc(-25,-15)} fill="none" stroke={C.warn}   strokeWidth="12" strokeOpacity=".85" />
          <path d={arc( 15, 25)} fill="none" stroke={C.warn}   strokeWidth="12" strokeOpacity=".85" />
          {/* Red ±25-85° */}
          <path d={arc(-85,-25)} fill="none" stroke={C.danger} strokeWidth="12" strokeOpacity=".85" />
          <path d={arc( 25, 85)} fill="none" stroke={C.danger} strokeWidth="12" strokeOpacity=".85" />
          {/* Needle */}
          {active && <>
            <line x1={cx} y1={cy} x2={np.x} y2={np.y}
              stroke={col} strokeWidth="3" strokeLinecap="round" />
            <circle cx={cx} cy={cy} r="6" fill={col} />
          </>}
          {/* Value */}
          <text x={cx} y={cy - 24} textAnchor="middle" fontSize="19" fontWeight="900"
            fill={active ? col : "rgba(255,255,255,0.2)"}>
            {active ? `${ag.toFixed(1)}°` : "—°"}
          </text>
          {/* G / D */}
          <text x={pt(-85).x - 6} y={pt(-85).y + 4} textAnchor="end"   fontSize="9" fill="rgba(255,255,255,0.3)">G</text>
          <text x={pt( 85).x + 6} y={pt( 85).y + 4} textAnchor="start" fontSize="9" fill="rgba(255,255,255,0.3)">D</text>
        </svg>
      </div>

      {active && (
        <div style={{
          background: `${col}22`, border: `1px solid ${col}44`,
          borderRadius: 10, padding: "9px 14px", marginBottom: 12, textAlign: "center",
          fontSize: 13, fontWeight: 700, color: col,
        }}>
          {status === "ok"   ? (t.incl_ok     || "✅ Inclinaison normale")                :
           status === "warn" ? (t.incl_warn   || "⚠️ Attention — inclinaison importante") :
                               (t.incl_danger || "🚨 DANGER — risque de basculement")}
        </div>
      )}

      <button onClick={() => setActive(a => !a)} style={{
        width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
        background: active ? `${C.danger}22` : C.mint,
        color: active ? C.danger : C.dark,
        fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
      }}>
        {active ? `⏹ ${t.desactiver || "Désactiver"}` : `▶ ${t.outil_incl_btn || "Activer l'inclinomètre"}`}
      </button>
    </div>
  );
}

/* ── Outil 3 : Boussole solaire ─────────────────────────────────── */
function BoussolesSolaire({ t = {} }) {
  const [active,     setActive]     = useState(false);
  const [heading,    setHeading]    = useState(0);
  const [gps,        setGps]        = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const headingBuffer = useRef([]);
  const lastUpdate = useRef(0);
  const lastTextUpdate = useRef(0);
  const compassRef = useRef(null);

  useEffect(() => {
    if (!active) {
      headingBuffer.current = [];
      lastUpdate.current = 0;
      lastTextUpdate.current = 0;
      return;
    }
    const h = (e) => {
      const now = Date.now();
      if (now - lastUpdate.current < 200) return;
      lastUpdate.current = now;

      const v = e.webkitCompassHeading != null
        ? e.webkitCompassHeading
        : (360 - (e.alpha || 0)) % 360;

      headingBuffer.current.push(v);
      if (headingBuffer.current.length > 30) {
        headingBuffer.current.shift();
      }

      const buffer = headingBuffer.current;
      const sinMean = buffer.reduce((s, a) => s + Math.sin(a * Math.PI / 180), 0) / buffer.length;
      const cosMean = buffer.reduce((s, a) => s + Math.cos(a * Math.PI / 180), 0) / buffer.length;
      const smoothed = (Math.atan2(sinMean, cosMean) * 180 / Math.PI + 360) % 360;

      if (compassRef.current) {
        compassRef.current.style.transform = `rotate(${-smoothed}deg)`;
      }

      if (now - lastTextUpdate.current >= 500) {
        lastTextUpdate.current = now;
        setHeading(smoothed);
      }
    };
    window.addEventListener("deviceorientationabsolute", h, true);
    window.addEventListener("deviceorientation",         h, true);
    return () => {
      window.removeEventListener("deviceorientationabsolute", h, true);
      window.removeEventListener("deviceorientation",         h, true);
    };
  }, [active]);

  const requestGps = () => {
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => { setGps({ lat: pos.coords.latitude, lon: pos.coords.longitude }); setGpsLoading(false); },
      ()  => setGpsLoading(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const now   = new Date();
  const decl  = [-20,-11,0,11,20,23,20,12,0,-12,-20,-23][now.getMonth()];
  const sunriseAz = ((90  - decl) + 360) % 360;
  const sunsetAz  = ((270 + decl) + 360) % 360;

  const cardinal = (az) => {
    const d = ["N","N-NE","NE","E-NE","E","E-SE","SE","S-SE","S","S-SO","SO","O-SO","O","O-NO","NO","N-NO"];
    return d[Math.round(((az % 360) + 360) % 360 / 22.5) % 16];
  };

  const W = 200, H = 200, cx = 100, cy = 100, R = 78;
  const cpt = (az, r) => {
    const rad = (az - 90) * Math.PI / 180;
    return { x: +(cx + r * Math.cos(rad)).toFixed(2), y: +(cy + r * Math.sin(rad)).toFixed(2) };
  };

  return (
    <div style={{ background: C.dark, borderRadius: 16, padding: 20 }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.mint, marginBottom: 12, textAlign: "center" }}>
        ☀️ {t.outil_boussole || "Boussole solaire"}
      </div>

      {!gps ? (
        <button onClick={requestGps} disabled={gpsLoading} style={{
          width: "100%", padding: "8px 0", borderRadius: 8, border: "none",
          background: "rgba(91,141,184,0.22)", color: "#90CAF9",
          fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 12,
        }}>
          {gpsLoading ? "📍 Localisation…" : `📍 ${t.activer_gps || "Activer la géolocalisation"}`}
        </button>
      ) : (
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", textAlign: "center", marginBottom: 10 }}>
          📍 {gps.lat.toFixed(3)}° {gps.lon.toFixed(3)}°
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
          <g ref={compassRef} style={{ transformOrigin: "100px 100px", transition: "transform 0.8s ease-out" }}>
            <circle cx={cx} cy={cy} r={R} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" />
            {Array.from({length:36}).map((_,i) => {
              const a = i * 10;
              const inner = cpt(a, R - (i % 9 === 0 ? 14 : 7));
              const outer = cpt(a, R - 1);
              return <line key={i} x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y}
                stroke="rgba(255,255,255,0.18)" strokeWidth="1" />;
            })}
            {[{l:"N",a:0,c:"#FF5555"},{l:"E",a:90,c:"rgba(255,255,255,.5)"},{l:"S",a:180,c:"rgba(255,255,255,.5)"},{l:"O",a:270,c:"rgba(255,255,255,.5)"}].map(d => {
              const p = cpt(d.a, R + 14);
              return <text key={d.l} x={p.x} y={p.y + 4} textAnchor="middle" fontSize="11" fontWeight="bold" fill={d.c}>{d.l}</text>;
            })}
            {/* Sunrise ← yellow */}
            {(() => { const tip = cpt(sunriseAz, R - 18), base = cpt((sunriseAz+180)%360, 20);
              return <line x1={base.x} y1={base.y} x2={tip.x} y2={tip.y} stroke="#FCD34D" strokeWidth="2.5" strokeLinecap="round" />; })()}
            {/* Sunset → orange */}
            {(() => { const tip = cpt(sunsetAz,  R - 18), base = cpt((sunsetAz+180)%360, 20);
              return <line x1={base.x} y1={base.y} x2={tip.x} y2={tip.y} stroke="#F97316" strokeWidth="2.5" strokeLinecap="round" />; })()}
          </g>
          <circle cx={cx} cy={cy} r="5" fill={active ? C.mint : "rgba(255,255,255,0.2)"} />
        </svg>
      </div>

      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", textAlign: "center", marginBottom: 8, lineHeight: 1.7 }}>
        🌅 {t.lever || "Lever"} : <strong style={{color:"#FCD34D"}}>{cardinal(sunriseAz)}</strong>
        {"  ·  "}
        🌇 {t.coucher || "Coucher"} : <strong style={{color:"#F97316"}}>{cardinal(sunsetAz)}</strong>
      </div>
      <div style={{
        background: "rgba(252,211,77,0.08)", border: "1px solid rgba(252,211,77,0.18)",
        borderRadius: 10, padding: "9px 14px", marginBottom: 14, textAlign: "center",
        fontSize: 12, color: "#FCD34D",
      }}>
        ☀️ {t.outil_conseil_solaire || "Garez-vous face au SUD pour des panneaux solaires optimaux"}
      </div>

      <button onClick={() => setActive(a => !a)} style={{
        width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
        background: active ? `${C.danger}22` : C.mint,
        color: active ? C.danger : C.dark,
        fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
      }}>
        {active ? `⏹ ${t.desactiver || "Désactiver"}` : `▶ ${t.outil_boussole_btn || "Activer la boussole"}`}
      </button>
    </div>
  );
}

/* ── Écran principal ─────────────────────────────────────────────── */
export default function OutilsScreen({ t = {} }) {
  return (
    <div style={{ background: C.bg, minHeight: "100%", paddingBottom: 32 }}>
      <div style={{ background: C.forest, padding: "20px 20px 18px" }}>
        <div style={{ fontSize: 22, fontWeight: 900, color: C.cream, textAlign: "center" }}>
          {t.outils_titre || "Outils de terrain"}
        </div>
        <div style={{ fontSize: 12, color: "rgba(248,246,241,0.6)", textAlign: "center", marginTop: 4 }}>
          {t.outils_sous_titre || "Capteurs de votre smartphone"}
        </div>
      </div>
      <div style={{ padding: "16px 16px 0", display: "flex", flexDirection: "column", gap: 16 }}>
        <NiveauBulle t={t} />
        <Inclinometre t={t} />
        <BoussolesSolaire t={t} />
      </div>
    </div>
  );
}
